import * as signalR from "@microsoft/signalr";
import { createCallHubConnection } from "@/calling/realtime/createCallHubConnection.js";
import { resolveApiBaseUrl } from "@/utils/apiBaseUrl";

const hubEntries = new Map();

const getConnectionKey = ({ guestSessionId, webContactToken } = {}) => {
  if (webContactToken) return `web_visitor:${webContactToken}`;
  if (guestSessionId) return `guest:${guestSessionId}`;
  return "staff";
};

const debugHub = (...args) => {
  if (import.meta.env?.DEV) {
    console.debug("[call-hub]", ...args);
  }
};

export const getStaffAccessToken = () => {
  return localStorage.getItem("accessToken") || "";
};

const getHubUrl = ({ guestSessionId, webContactToken } = {}) => {
  const baseUrl = resolveApiBaseUrl();
  const hubUrl = `${baseUrl.replace(/\/$/, "")}/hub`;
  const params = new URLSearchParams();

  if (guestSessionId) params.set("sessionId", guestSessionId);
  if (webContactToken) params.set("contactToken", webContactToken);

  const query = params.toString();
  return query ? `${hubUrl}?${query}` : hubUrl;
};

export const createRealtimeHubConnection = ({ guestSessionId, webContactToken } = {}) => {
  const isAnonymous = Boolean(guestSessionId || webContactToken);
  const staffToken = isAnonymous ? "" : getStaffAccessToken();
  const hubUrl = getHubUrl({ guestSessionId, webContactToken });

  if (!isAnonymous && !staffToken) {
    throw new Error("Staff hub connection requires an access token.");
  }

  debugHub("creating connection", {
    hubUrl,
    mode: webContactToken ? "web_visitor" : isAnonymous ? "guest" : "staff",
    hasStaffToken: isAnonymous ? false : Boolean(staffToken),
  });

  return createCallHubConnection({
    hubUrl,
    accessTokenFactory: () => (isAnonymous ? "" : getStaffAccessToken()),
    automaticReconnect: true,
    logger: signalR.LogLevel.Warning,
  });
};

export const getRealtimeHubConnection = (options = {}) => {
  const key = getConnectionKey(options);
  const isAnonymous = Boolean(options?.guestSessionId || options?.webContactToken);
  const staffToken = isAnonymous ? "" : getStaffAccessToken();
  let entry = hubEntries.get(key);

  if (!isAnonymous && !staffToken) {
    if (entry) {
      entry.connection.stop().catch(() => { });
      hubEntries.delete(key);
    }
    throw new Error("Staff hub connection requires an access token.");
  }

  if (!isAnonymous && entry?.staffToken && entry.staffToken !== staffToken) {
    debugHub("staff token changed; recreating connection");
    entry.intentionallyStopped = true;
    entry.startPromise = null;
    const previous = entry.connection;
    hubEntries.delete(key);
    entry = null;
    previous.stop().catch(() => { });
  }

  if (!entry) {
    entry = {
      key,
      connection: createRealtimeHubConnection(options),
      startPromise: null,
      intentionallyStopped: false,
      staffToken: isAnonymous ? "" : staffToken,
    };
    hubEntries.set(key, entry);
  }

  return entry.connection;
};

export const startRealtimeHubConnection = async (options = {}) => {
  const isAnonymous = Boolean(options?.guestSessionId || options?.webContactToken);
  const key = getConnectionKey(options);
  if (!isAnonymous && !getStaffAccessToken()) {
    debugHub("staff start skipped: missing token");
    throw new Error("Staff hub connection requires an access token.");
  }

  const connection = getRealtimeHubConnection(options);
  const entry = hubEntries.get(key);

  if (
    connection.state === signalR.HubConnectionState.Connected ||
    connection.state === signalR.HubConnectionState.Connecting ||
    connection.state === signalR.HubConnectionState.Reconnecting
  ) {
    debugHub("already active", key, connection.state);
    return entry?.startPromise || connection;
  }

  if (entry?.startPromise) {
    debugHub("start already pending", key);
    return entry.startPromise;
  }

  if (entry) {
    entry.intentionallyStopped = false;
    debugHub("start requested", {
      key,
      mode: options?.guestSessionId ? "guest" : "staff",
      hasStaffToken: options?.guestSessionId ? false : Boolean(getStaffAccessToken()),
      hubUrl: getHubUrl(options),
    });
    entry.startPromise = connection.start()
      .then(() => {
        debugHub("connected", key);
        return connection;
      })
      .catch((error) => {
        debugHub("failed", key, error?.message || error);
        throw error;
      })
      .finally(() => {
        const current = hubEntries.get(key);
        if (current) current.startPromise = null;
      });

    return entry.startPromise;
  }

  return connection;
};

export const stopStaffRealtimeHubConnection = async () => {
  await stopRealtimeHubConnection({}, { force: true });
};

export const stopRealtimeHubConnection = async (options = {}, { force = false } = {}) => {
  const key = getConnectionKey(options);
  const entry = hubEntries.get(key);

  if (!entry) return;

  if (!force) {
    return;
  }

  entry.intentionallyStopped = true;
  entry.startPromise = null;

  if (entry.connection.state !== signalR.HubConnectionState.Disconnected) {
    await entry.connection.stop().catch(() => { });
  }

  hubEntries.delete(key);
  debugHub("stopped", key);
};
